import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { useB2BMode } from "../hooks/useB2BMode";
import { ConfirmModal } from "../components/ui";
import { enterpriseLogin } from "../services/api";
import { apiFetch } from "../services/apiClient";
import "./LoginPage.scss";
import logoMark from "../logo/Symbol_32.svg";
import logoFull from "../logo/Logo_32.svg";
import loginFeature1 from "../assets/images/login_feature1.svg";
import loginFeature2 from "../assets/images/login_feature2.png";
import loginFeature3 from "../assets/images/login_feature3.svg";
import loginFeature4 from "../assets/images/login_feature4.svg";

const ENTERPRISE_LOGIN_ERROR = "이메일 또는 비밀번호를 확인해 주세요";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginPage = () => {
  const [googleClientId, setGoogleClientId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordFindOpen, setPasswordFindOpen] = useState(false);

  // 기업 로그인 폼 상태
  const [enterpriseEmail, setEnterpriseEmail] = useState("");
  const [enterprisePassword, setEnterprisePassword] = useState("");

  const navigate = useNavigate();
  const { loginUser } = useAppContext();
  const { enableB2BMode, disableB2BMode } = useB2BMode();

  useEffect(() => {
    // 세션 만료 메시지 체크
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_expired') === 'true') {
      setError('세션이 만료되었습니다. 다시 로그인해주세요.');
    }

    // 구글 OAuth 설정 가져오기
    apiFetch("/auth/google/config", { method: "GET" })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setGoogleClientId(data.google_client_id);
        } else {
          console.error("구글 설정 실패:", data.error);
        }
      })
      .catch((err) => {
        console.error("구글 설정 로드 실패:", err);
      });
  }, []);

  const handlePostLogin = (userData, accessToken) => {
    loginUser(userData, accessToken);

    // password_reset_required 체크
    if (userData.password_reset_required) {
      navigate("/change-password?required=true", { replace: true });
      return;
    }

    // tier 기반 리다이렉트
    const isSuperAdmin = userData.tier === 'super';
    const isEnterprise = userData.account_type === 'business' && userData.tier === 'enterprise';
    if (isSuperAdmin) {
      navigate("/super-admin");
    } else if (isEnterprise) {
      enableB2BMode();
      navigate("/b2b");
    } else {
      enableB2BMode();
      navigate("/b2b");
    }
  };

  const handleGoogleLogin = () => {
    if (!googleClientId) {
      setError("구글 OAuth 설정이 아직 로드되지 않았습니다.");
      return;
    }

    setLoading(true);
    setError("");

    const redirectUri = `${window.location.origin}/auth/callback`;

    const popup = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${encodeURIComponent(
        redirectUri
      )}&response_type=id_token&scope=openid%20email%20profile&nonce=${Date.now()}`,
      "googleAuth",
      "width=500,height=600,scrollbars=yes,resizable=yes"
    );

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        setLoading(false);
      }
    }, 1000);

    const messageListener = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "GOOGLE_AUTH_SUCCESS") {
        clearInterval(checkClosed);
        popup.close();
        window.removeEventListener("message", messageListener);
        verifyGoogleToken(event.data.token);
      } else if (event.data.type === "GOOGLE_AUTH_ERROR") {
        clearInterval(checkClosed);
        popup.close();
        window.removeEventListener("message", messageListener);
        setError("구글 로그인에 실패했습니다.");
        setLoading(false);
      }
    };

    window.addEventListener("message", messageListener);
  };

  const verifyGoogleToken = async (token) => {
    try {
      const response = await apiFetch("/auth/google/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (data.success) {
        // access_token은 호환용으로만 참고, 저장하지 않음 (HttpOnly Cookie 사용)
        handlePostLogin(data.user, null);
      } else {
        setError(data.error || "로그인에 실패했습니다.");
      }
    } catch (err) {
      console.error("토큰 검증 실패:", err);
      setError("로그인 처리 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleEnterpriseLogin = async (e) => {
    e.preventDefault();
    const email = enterpriseEmail.trim();
    if (!email || !enterprisePassword) {
      setError("이메일과 비밀번호를 모두 입력해주세요.");
      return;
    }
    if (!EMAIL_PATTERN.test(email)) {
      setError(ENTERPRISE_LOGIN_ERROR);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await enterpriseLogin(email, enterprisePassword);
      if (data.success) {
        // access_token은 호환용으로만 참고, 저장하지 않음 (HttpOnly Cookie 사용)
        handlePostLogin(data.user, null);
      } else {
        setError(ENTERPRISE_LOGIN_ERROR);
      }
    } catch (err) {
      console.error("기업 로그인 실패:", err);
      setError(ENTERPRISE_LOGIN_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordFind = () => {
    setPasswordFindOpen(true);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-left">
          <div className="login-content">
            <div className="login-header">
              <img src={logoFull} alt="ReOpsAI" className="login-logo" />
            </div>

            <div className="login-form">
              {error && <div className="error-message">{error}</div>}

              <form onSubmit={handleEnterpriseLogin} className="enterprise-login-form" noValidate>
                <div className="form-group">
                  <label htmlFor="enterprise-email">ID (이메일)</label>
                  <input
                    id="enterprise-email"
                    type="text"
                    value={enterpriseEmail}
                    onChange={(e) => setEnterpriseEmail(e.target.value)}
                    placeholder="회사 이메일 주소 입력"
                    disabled={loading}
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="enterprise-password">비밀번호</label>
                  <input
                    id="enterprise-password"
                    type="password"
                    value={enterprisePassword}
                    onChange={(e) => setEnterprisePassword(e.target.value)}
                    placeholder="비밀번호 입력"
                    disabled={loading}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="enterprise-login-submit-btn"
                  disabled={loading}
                >
                  {loading ? '로그인 중...' : '로그인'}
                </button>
              </form>

              <button
                type="button"
                className="password-find-btn"
                onClick={handlePasswordFind}
                disabled={loading}
              >
                비밀번호 찾기
              </button>

              <div className="individual-login-preserved" aria-hidden="true">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={loading || !googleClientId}
                  tabIndex={-1}
                >
                  구글 계정으로 계속하기
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="login-right">
          <div className="login-right-bg-shape login-right-bg-shape-top" />
          <div className="login-right-bg-shape login-right-bg-shape-bottom" />
          <div className="login-showcase">
            <div className="login-showcase-header">
              <img src={logoMark} alt="" className="login-showcase-logo" />
              <h2>ReOps와 함께<br />가장 빠르고 깊이 있는 리서치를 경험하세요.</h2>
              <p>복잡한 리서치 설계부터 실시간 인터뷰까지,<br />ReOps AI가 인사이트 발굴의 전 과정을 돕습니다.</p>
            </div>

            <div className="login-feature-grid" aria-label="ReOpsAI 주요 기능">
              <div className="login-feature-card">
                <img
                  src={loginFeature1}
                  alt="인터뷰 설계부터 가이드라인까지 한 번에 생성하는 리서치 계획"
                  className="login-feature-card-image"
                  loading="eager"
                />
              </div>
              <div className="login-feature-card">
                <img
                  src={loginFeature2}
                  alt="UX 태도와 도메인 DNA를 반영한 맞춤형 AI 페르소나 생성"
                  className="login-feature-card-image"
                  loading="eager"
                />
              </div>
              <div className="login-feature-card">
                <img
                  src={loginFeature3}
                  alt="최적화된 AI 퍼소나를 통한 빠른 화면 사용성 검증"
                  className="login-feature-card-image"
                  loading="eager"
                />
              </div>
              <div className="login-feature-card">
                <img
                  src={loginFeature4}
                  alt="AI 퍼소나의 일관된 응답을 통해 인사이트 찾는 1:1 인터뷰"
                  className="login-feature-card-image"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={passwordFindOpen}
        type="info"
        title="비밀번호 찾기 안내"
        message="비밀번호를 잊으셨다면 사내 관리자에게 비밀번호 초기화를 요청해주세요."
        confirmText="확인"
        onConfirm={() => setPasswordFindOpen(false)}
        onCancel={() => setPasswordFindOpen(false)}
        size="sm"
        align="dialog"
      />
    </div>
  );
};

export default LoginPage;
